import { ArrowExpandOutlineIcon, BaseThemeType, IconButton, styled, useMediaQuery } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC } from 'react'
import ContentSlider from './common/ContentSlider'
import ResourcesCard from './common/ResourcesCard'
import ResourcesLinks from './common/ResourcesLinks'
import { SectionWrapper } from './common/styles'

interface Resource {
  id: number | string
  title: string
  url: string
}

interface PropTypes {
  resources: Resource[]
  header?: string
  onExpand?: () => void
  onResourceClick?: (url: string) => void
}

const Header = styled('div', { name: 'Header' })({
  display: 'flex',
  alignItems: 'center',
})

const HeaderText = styled('h1', { name: 'HeaderText' })({
  fontSize: 24,
  margin: 0,
  marginRight: 'auto',
})

const ResourcesContainer = styled('div', { name: 'ResourcesContainer' })(({ theme }) => ({
  marginTop: 30,
  display: 'flex',
  gap: 20,
  flexWrap: 'nowrap',
  boxSizing: 'border-box',
  minHeight: 400,
  overflowX: 'auto',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginTop: 40,
    minHeight: 200,
  },
}))

const ExpandControl = ({ onClick, label }: { onClick: () => void; label: string }) => {
  return (
    <IconButton aria-label={label} title={label} onClick={onClick}>
      <ArrowExpandOutlineIcon variant={1} />
    </IconButton>
  )
}

const Resources: FC<PropTypes> = ({ resources, header, onExpand, onResourceClick }) => {
  const translate = useTranslate()
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const sectionHeader = header || translate('Resources & Datasheets')
  const expandLabel = translate('Expand resources')
  if (!resources || !resources.length) return null

  return (
    <SectionWrapper>
      {isMobile ? (
        <>
          <Header>
            <HeaderText>{sectionHeader}</HeaderText>
            {onExpand && <ExpandControl onClick={onExpand} label={expandLabel} />}
          </Header>
          <ResourcesContainer>
            {resources.map((resource, i) => (
              <div key={resource.id || i} style={{ flexShrink: 0 }}>
                <ResourcesLinks
                  resource={resource}
                  onClick={() => {
                    onResourceClick?.(resource.url)
                  }}
                />
              </div>
            ))}
          </ResourcesContainer>
        </>
      ) : (
        <ContentSlider
          cards={resources}
          header={sectionHeader}
          render={(resource: Resource) => (
            <ResourcesCard
              resource={resource}
              onClick={() => {
                onResourceClick?.(resource.url)
              }}
            />
          )}
          CTAButtonLeftSlider={onExpand ? <ExpandControl onClick={onExpand} label={expandLabel} /> : undefined}
        />
      )}
    </SectionWrapper>
  )
}

export default Resources
